import React, { useMemo, useEffect } from 'react';

import {
  EmmaCampaignCard,
  EmmaCarousel,
  useBreakpoint,
} from '@axa-asia/emma-stylesheet';
import './styles.scss';
import { useSelector } from 'react-redux';
import { getCmsProfile } from '../../../src/selectors/userSelectors';
import prismicApiClient from '../../../src/utils/prismicApi';
import {
  formatPromoCards,
  gaBannerClick,
} from '../../modules/offers/utils/prismicBannerUtils';

const GA_BANNER_TITLE_PREFIX = 'web';

type Props = {
  lang: 'en' | 'zh';
  gaLob: string;
};

export const EmmaPromoBanner = ({ lang }: Props) => {
  const CWP_PROFILES = ['CWP'];
  const cmsProfiles = useSelector(getCmsProfile) || CWP_PROFILES;

  const eventAndCampaignQuery = prismicApiClient
    .getOfferBanners({
      lang: lang,
      platform: 'WEB',
      tags: cmsProfiles,
      preloginOnly: 'POSTLOGIN_ONLY',
      myPrivilegesTab: 'EVENTS_AND_CAMPAIGNS',
      limit: 15,
    })
    .useHook();

  const productOffersQuery = prismicApiClient
    .getOfferBanners({
      lang: lang,
      platform: 'WEB',
      tags: cmsProfiles,
      preloginOnly: 'POSTLOGIN_ONLY',
      myPrivilegesTab: 'AXA_PRODUCT_OFFERS',
      limit: 15,
    })
    .useHook();

  const bp = useBreakpoint();

  const eventAndCampaignCards = useMemo(() => {
    if (!eventAndCampaignQuery.data) {
      return [];
    }

    return formatPromoCards(
      eventAndCampaignQuery.data,
      lang,
      cmsProfiles,
      'events',
      undefined,
      GA_BANNER_TITLE_PREFIX,
    );
  }, [eventAndCampaignQuery.data, lang, cmsProfiles]);

  const productOffersCards = useMemo(() => {
    if (!productOffersQuery.data) {
      return [];
    }
    return formatPromoCards(
      productOffersQuery.data,
      lang,
      cmsProfiles,
      'offers',
      undefined,
      GA_BANNER_TITLE_PREFIX,
    );
  }, [productOffersQuery.data, lang, cmsProfiles]);

  const newAndPromotionCards = useMemo(() => {
    if (eventAndCampaignQuery.isLoading || productOffersQuery.isLoading) {
      return [];
    }
    return [...productOffersCards, ...eventAndCampaignCards]
      ?.sort((a, b) => {
        const sequenceA = a.sequence || 0;
        const sequenceB = b.sequence || 0;
        return sequenceA - sequenceB;
      })
      .slice(0, 5);
  }, [productOffersCards, eventAndCampaignCards]);

  useEffect(() => {
    if (!newAndPromotionCards || !(newAndPromotionCards?.length > 0)) {
      return;
    }
    newAndPromotionCards.map((item) => {
      gaBannerClick(
        cmsProfiles,
        `web - ${item.title}`,
        'display',
        item.gaCampaignName,
        item.gaBannerCategory,
        item.gaCustomBannerCategory,
      );
    });
  }, [newAndPromotionCards]);

  return (
    <div className="emma-news" key={bp}>
      <EmmaCarousel
        slidesPerViewLg={3}
        slidesPerView={1}
        shadowMargin
        disableMargin
      >
        {newAndPromotionCards.map((value, index) => {
          return (
            <div key={index} className="emma-promo-banner-card-wrapper">
              <EmmaCampaignCard {...value} />
            </div>
          );
        })}
        {Array.from(
          { length: bp === 'lg' ? 3 - newAndPromotionCards.length : 0 },
          (_, index) => (
            <div key={`empty-${index}-${bp}`} />
          ),
        )}
      </EmmaCarousel>
    </div>
  );
};
